import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';
import { capitalize } from 'humanize-plus';
import moment from 'moment';
import { filter, Subject, takeUntil } from 'rxjs';
import { v4 as UUIDv4 } from 'uuid';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import {
  Filter,
  AnalyticItemFilterSets,
  FilterService,
  FilterSet,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { DateMarker } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { ActionPlanSidePaneData, Comparison } from 'app/providers/bridge-api/dashboard.service';
import { ActionPlan } from 'app/providers/storage-api/actionplan.service';
import { DocumentService, FacetList } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { PredictiveService } from 'app/providers/storage-api/predictive.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { StringBuilder } from 'app/utils/string-builder';

import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import SidePaneItem from '../dashboard-side-pane/SidePaneItem';
import { ActionPlanCacheService } from '../../services/action-plan-cache.service';
import { DatasourceManagerStore } from '../../stores/datasource-manager.store';
import { createWidgetGroup } from '../../stores/widget-group.store';

interface FilterPill {
  filter: Filter;
  text: string;
  textExtra: string;
  shortLabel: string;
  dataType: string;
  comparisonType: 'date' | 'keyword' | 'numeric' | undefined;
  dropdownObjects: DropdownObject[];
}

export interface AddToComparisonActionData {
  type: 'date' | 'keyword' | 'numeric';
  filter: Filter;
}

export interface AddToWidgetGroupActionData {
  filter: Filter;
  isBreakdown: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-side-pane-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './action-plan-side-pane-item.component.html',
  styleUrls: ['./action-plan-side-pane-item.component.scss'],
})
export class ActionPlanSidePaneItemComponent
  extends SidePaneItem<ActionPlanSidePaneData>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);

  // eslint-disable-next-line
  @Input('item') actionPlanSidePaneData: ActionPlanSidePaneData;

  itemType = 'actionPlan';

  actionPlan: ActionPlan;
  textAnalysisDataSource: WidgetDataSource;
  name: string;
  hasBreakdown: boolean;
  hasFilter: boolean;
  filterPills: FilterPill[];
  hasWeightField: boolean;
  weightFieldName: string;
  infoTooltip: string;

  isReadOnly = false;

  breakdownDs: DropdownObject[];
  driverDs: DropdownObject[];
  targetDs: DropdownObject[];
  weightDs: DropdownObject[];
  createdDateDs: DropdownObject[];
  dueDateDs: DropdownObject[];

  private readonly destroy$ = new Subject<void>();

  constructor(
    authService: AuthService,
    private actionPlanCacheService: ActionPlanCacheService,
    private alertService: AlertService,
    private translocoService: TranslocoService,
    private filterService: FilterService,
    private documentService: DocumentService,
    private predictiveService: PredictiveService
  ) {
    super();

    this.isReadOnly = authService.isReadOnly;

    actionPlanCacheService.update$
      .pipe(
        takeUntil(this.destroy$),
        filter((a) => a.plan_id === this.item.id)
      )
      .subscribe(() => this.loadActionPlan());
  }

  async ngOnInit(): Promise<void> {
    this.loadActionPlan();

    await this.setDataSource();

    this.translocoService
      .selectTranslateObject('action-plan-side-pane-item')
      .pipe(takeUntil(this.destroy$))
      .subscribe((o) => {
        this.setDropdowns(o);
        this.setFilters(o);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onToggleExpand(): void {
    this.itemAction.emit({ action: 'expand', data: !this.isExpanded });
  }

  onRemove(): void {
    this.itemAction.emit({ action: 'delete', data: this.actionPlanSidePaneData });
  }

  async onBreakdownAction(id: string): Promise<void> {
    switch (id) {
      case 'add_wg':
        {
          const filterObj = this.createKeywordFilter(
            this.actionPlan.metadata.breakdownColumn,
            this.actionPlan.metadata.breakdown
          );

          const data: AddToWidgetGroupActionData = { filter: filterObj, isBreakdown: true };

          this.itemAction.emit({ action: 'add_wg', data });
        }
        break;
      case 'create_wg':
        {
          const wg = createWidgetGroup(
            FieldService.getFieldLongName(this.actionPlan.metadata.breakdownColumn),
            [],
            this.currentDashboardPageId
          );

          wg.filters.metadata.and.push(
            this.createKeywordFilter(
              this.actionPlan.metadata.breakdownColumn,
              this.actionPlan.metadata.breakdown
            )
          );

          this.itemAction.emit({ action: 'create_wg', data: wg });
        }
        break;
      case 'create_c':
        {
          const comparison: Comparison = {
            id: UUIDv4(),
            title: FieldService.getFieldLongName(this.actionPlan.metadata.breakdownColumn),
            type: 'keyword',
            color: `rgba(37, 55, 77, 1)`,
            filters: [],
            page: this.currentDashboardPageId,
            widgets: [],
          };

          const keywords = await this.getKeywords();
          keywords.forEach((k) =>
            comparison.filters.push({
              and: [this.createKeywordFilter(this.actionPlan.metadata.breakdownColumn, k)],
              or: [],
              not: [],
              label: k,
              id: UUIDv4(),
            })
          );

          this.itemAction.emit({ action: 'create_c', data: comparison });
        }
        break;
    }
  }

  onDriverAction(id: string): void {
    switch (id) {
      case 'set_item':
        this.itemAction.emit({ action: 'set_item', data: this.actionPlan.metadata.driverColumn });
        break;
    }
  }

  onTargetAction(id: string): void {
    switch (id) {
      case 'set_goal':
        this.itemAction.emit({
          action: 'set_goal',
          data: Utils.round(this.actionPlan.metric_target_value),
        });
        break;
    }
  }

  onWeightAction(id: string): void {
    switch (id) {
      case 'set_weight':
        this.itemAction.emit({
          action: 'set_weight',
          data: this.actionPlan.metadata.weightField,
        });
        break;
    }
  }

  onDateAction(id: string): void {
    let actionData: DateMarker;

    switch (id) {
      case 'set_date_marker_created':
        actionData = {
          displayName: 'Created',
          color: {
            red: 0,
            green: 0,
            blue: 255,
            alpha: 1,
          },
          date: this.actionPlan.creation_date,
        };
        break;
      case 'set_date_marker_due':
        actionData = {
          displayName: 'Due',
          color: {
            red: 0,
            green: 255,
            blue: 0,
            alpha: 1,
          },
          date: this.actionPlan.due_date,
        };
        break;
    }

    this.itemAction.emit({ action: 'set_date_marker', data: actionData });
  }

  onFilterAction(id: string, filterPill: FilterPill): void {
    switch (id) {
      case 'add_wg':
        const data: AddToWidgetGroupActionData = { filter: filterPill.filter, isBreakdown: false };

        this.itemAction.emit({ action: 'add_wg', data });
        break;
      case 'create_wg':
        {
          const wg = createWidgetGroup(
            capitalize(filterPill.dataType),
            [],
            this.currentDashboardPageId
          );

          wg.filters.metadata.and.push(filterPill.filter);

          this.itemAction.emit({ action: 'create_wg', data: wg });
        }
        break;
      case 'add_c':
        {
          const compData: AddToComparisonActionData = {
            type: filterPill.comparisonType,
            filter: filterPill.filter,
          };

          this.itemAction.emit({ action: 'add_c', data: compData });
        }
        break;
      case 'create_c':
        {
          const comparison: Comparison = {
            id: UUIDv4(),
            title: capitalize(filterPill.dataType),
            type: filterPill.comparisonType,
            color: `rgba(37, 55, 77, 1)`,
            filters: [],
            page: this.currentDashboardPageId,
            widgets: [],
          };

          comparison.filters.push({
            and: [filterPill.filter],
            or: [],
            not: [],
            label: capitalize(filterPill.dataType),
            id: UUIDv4(),
          });

          this.itemAction.emit({ action: 'create_c', data: comparison });
        }
        break;
      case 'add_f':
        this.itemAction.emit({ action: 'add_f', data: filterPill.filter });
        break;
    }
  }

  private async loadActionPlan(): Promise<void> {
    try {
      await this.actionPlanCacheService.waitUntilLoaded;

      this.actionPlan = this.actionPlanCacheService.get(this.actionPlanSidePaneData.id);

      this.name = `${this.actionPlan.metadata.breakdown} / ${this.actionPlan.metric_name}`;

      this.hasBreakdown = this.actionPlan.metadata.breakdown !== Constants.TWTRA_ALL_BREAKDOWN;
      this.hasWeightField = !!this.actionPlan.metadata.weightField;

      if (this.hasWeightField) {
        this.weightFieldName = FieldService.getFieldLongName(this.actionPlan.metadata.weightField);
      }

      this.setInfoTooltip();
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('action-plan-side-pane-item.load-failure'),
        error
      );
    }
  }

  private async setDataSource(): Promise<void> {
    const dataSources = this.datasourceManagerStore.getByCollectionName(
      this.actionPlan.metadata.collectionId
    );

    const calculation = await this.predictiveService.getCalculationById(
      this.actionPlan.metadata.projectId,
      this.actionPlan.metadata.collectionId,
      this.actionPlan.metadata.calculationId
    );

    this.textAnalysisDataSource = dataSources.find(
      (ds) => ds.analysisId === calculation.request.properties.analysisId
    );
  }

  private createKeywordFilter(columnId: string, value: string): Filter {
    return {
      analyticItem: `metadata.${columnId}`,
      value,
    };
  }

  private async getKeywords(): Promise<string[]> {
    const field = `metadata.${this.actionPlan.metadata.breakdownColumn}`;

    const filterOptions = {
      widgetDataSource: this.textAnalysisDataSource,
      analyticItem: field,
      type: 'documentCount',
      drilledDownOn: 'none',
      customFieldSelected: true,
      standardFieldSelected: false,
      filters: FilterService.getDefaultFilterSet(),
      sentimentScale: 3,
      selectedItems: [],
    } as WidgetQueryOptions;

    const query = this.filterService.getQueryForDocuments(filterOptions, false, true, true);

    let facetList: FacetList = {};
    if (this.textAnalysisDataSource.type === 'analysis') {
      facetList = await this.documentService.getFacetsWithinAnalysis(
        this.textAnalysisDataSource.collectionName,
        this.textAnalysisDataSource.analysisId,
        query,
        undefined,
        undefined,
        undefined,
        this.actionPlan.metadata.projectId
      );
    } else {
      facetList = await this.documentService.getFacetsWithinGroup(
        this.actionPlan.metadata.projectId,
        this.textAnalysisDataSource,
        query
      );
    }

    const results: string[] = [];

    if (facetList[field]) {
      results.push(
        ...Object.keys(facetList[field])
          .filter((k) => k)
          .slice(0, 10)
      );
    }

    return results;
  }

  private setInfoTooltip(): void {
    const format = 'DD-MM-YYYY';

    const sb = new StringBuilder();
    sb.appendLine(
      translate('action-plan-side-pane-item.expires', {
        Moment: moment(this.actionPlan.due_date).format(format),
      })
    );
    sb.appendLine(
      translate('action-plan-side-pane-item.created-on', {
        Moment: moment(this.actionPlan.creation_date).format(format),
      })
    );
    sb.appendLine(
      translate('action-plan-side-pane-item.created-by', {
        person: this.actionPlan.creator_user_name,
      })
    );

    this.infoTooltip = sb.toString();
  }

  private setDropdowns(translateObject: { [key: string]: string }): void {
    this.breakdownDs = [
      {
        title: translateObject['add-to-widget-group'],
        id: 'add_wg',
      },
      {
        title: translateObject['create-widget-group'],
        id: 'create_wg',
      },
      {
        title: translateObject['create-comparison-with-all'],
        id: 'create_c',
      },
    ];

    this.driverDs = [
      {
        title: translateObject['set-as-analytic-item'],
        id: 'set_item',
      },
    ];

    this.targetDs = [
      {
        title: translateObject['set-as-goal'],
        id: 'set_goal',
      },
    ];

    this.weightDs = [
      {
        title: translateObject['apply-weight'],
        id: 'set_weight',
      },
    ];

    this.createdDateDs = [
      {
        title: translateObject['set-date-marker'],
        id: 'set_date_marker_created',
      },
    ];

    this.dueDateDs = [
      {
        title: translateObject['set-date-marker'],
        id: 'set_date_marker_due',
      },
    ];
  }

  private setFilters(translateObject: { [key: string]: string }): void {
    let documentFilterSets: AnalyticItemFilterSets;
    let comparisonFilterSet: FilterSet;
    let wgFilterSets: AnalyticItemFilterSets;

    const filters: Filter[] = [];

    if (this.actionPlan.metadata.filters) {
      documentFilterSets = JSON.parse(this.actionPlan.metadata.filters);
    }

    if (this.actionPlan.metadata.comparisonFilters) {
      comparisonFilterSet = JSON.parse(this.actionPlan.metadata.comparisonFilters);
    }

    if (this.actionPlan.metadata.widgetGroupFilters) {
      wgFilterSets = JSON.parse(this.actionPlan.metadata.widgetGroupFilters);
    }

    if (documentFilterSets) {
      Object.keys(documentFilterSets).forEach((k) => {
        const filterSet: FilterSet = documentFilterSets[k];
        filters.push(...filterSet.and, ...filterSet.or, ...filterSet.not);
      });
    }

    if (comparisonFilterSet) {
      filters.push(...comparisonFilterSet.and);
    }

    if (wgFilterSets) {
      Object.keys(wgFilterSets).forEach((k) => {
        const filterSet: FilterSet = wgFilterSets[k];
        filters.push(...filterSet.and, ...filterSet.or, ...filterSet.not);
      });
    }

    this.hasFilter = !!filters.length;
    this.filterPills = filters.map((f) => {
      const pillTexts = this.filterService.getPillTexts(f, 30);
      const dataType = this.getFilterDataType(f);
      const comparisonType = this.getComparisonType(dataType);
      const canBeUsedInComparison = !!comparisonType;

      return {
        filter: f,
        text: pillTexts.text,
        textExtra: pillTexts.textExtra,
        shortLabel: pillTexts.shortLabel,
        dataType,
        comparisonType,
        dropdownObjects: this.getFilterDropdown(translateObject, canBeUsedInComparison),
      };
    });
  }

  private getFilterDropdown(
    translateObject: { [key: string]: string },
    canBeUsedInComparison: boolean
  ): DropdownObject[] {
    return [
      {
        title: translateObject['add-to-widget-group'],
        id: 'add_wg',
      },
      {
        title: translateObject['create-widget-group'],
        id: 'create_wg',
      },
      {
        title: translateObject['add-to-comparison'],
        id: 'add_c',
        disabled: !canBeUsedInComparison,
      },
      {
        title: translateObject['create-comparison'],
        id: 'create_c',
        disabled: !canBeUsedInComparison,
      },
      {
        title: translateObject['add-to-widget-filter'],
        id: 'add_f',
      },
    ];
  }

  private getFilterDataType(fi: Filter): string {
    if (fi.analyticItem.startsWith('metadata.')) {
      return FieldService.getFieldType(fi.analyticItem.split('.')[1]);
    }

    if (fi.analyticItem === 'createdAt') {
      return 'date';
    }

    return this.filterService.getFilterConfigItem(fi.analyticItem).title;
  }

  private getComparisonType(filterDataType: string): 'date' | 'keyword' | 'numeric' | undefined {
    switch (filterDataType) {
      case 'date':
        return 'date';
      case 'keyword':
        return 'keyword';
      case 'long':
      case 'double':
      case 'weight':
        return 'numeric';
      default:
        return undefined;
    }
  }

  get item(): ActionPlanSidePaneData {
    return this.actionPlanSidePaneData;
  }

  get isInactive(): boolean {
    return this.isReadOnly || this.isDashboardLocked;
  }
}
